import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { CopyIcon } from "@phosphor-icons/react/dist/csr/Copy";
import { useState } from "react";
import { useCopyToClipboard, useDebounceCallback } from "usehooks-ts";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { toast } from "@volca/ui/lib/toast";

const COPIED_FOR_MS = 1600;

/**
 * The business's AP inbox, above the emails it received. Onboarding shows it once,
 * and after that it is what a customer comes back for when adding a vendor to their
 * forwarding rule, so it sits beside the list it feeds.
 */
export const ApAddress = ({ address }: { address: string }) => {
  const [, copyToClipboard] = useCopyToClipboard();
  const [copied, setCopied] = useState(false);
  // Debounced, so each copy restarts the wait and the check lasts from the latest one.
  // It takes the stable setter: a new function each render would get its own timer.
  const clearCopied = useDebounceCallback(setCopied, COPIED_FOR_MS);

  const copy = async () => {
    if (await copyToClipboard(address)) {
      setCopied(true);
      clearCopied(false);
    } else {
      clearCopied.cancel();
      setCopied(false);
      toast.error("Couldn't copy the address", { description: address });
    }
  };

  return (
    <Stack direction="row" gap={2} align="center" className="min-w-0">
      <Text tone="muted" className="shrink-0">
        Forward invoices to
      </Text>
      <Text weight="medium" truncate className="min-w-0 select-all">
        {address}
      </Text>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-xs"
              type="button"
              onClick={() => void copy()}
              aria-label={`Copy AP inbox address, ${address}`}
            />
          }
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
        </TooltipTrigger>
        <TooltipContent>{copied ? "Copied" : "Copy"}</TooltipContent>
      </Tooltip>
      {/* The icon swap is visual only; this says it to a screen reader too. */}
      <span role="status" className="sr-only">
        {copied && "Address copied"}
      </span>
    </Stack>
  );
};
